import {FC, useState, useRef, ChangeEvent, useContext} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {useSetRecoilState} from 'recoil';
import {useParams} from 'react-router-dom';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFnsV3';
import {ru} from 'date-fns/locale';

import {Button, Modal, Typography, Toolbar, Tooltip} from '@mui/material';
import {Box, Stack} from '@mui/system';
import LoadingButton from '@mui/lab/LoadingButton';
import DocIconSwitcher from '../../../../ui/doc-icon-switcher/DocIconSwitcher';
import ControlledInput from '../../../../ui/controlled-input/ControlledInput';
import {DateTimePicker, LocalizationProvider} from '@mui/x-date-pickers';

import {useFileUpload} from '../../../../hooks/useFileUpload';

import {green} from '@mui/material/colors';
import {colors} from '../../../../shared/colors';

import {EventBusContext} from '../../../../providers/EventBus';
import {triggerReasonDelays} from '../../../../store/service-requests/atoms';
import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';

interface IAddDelayForm {
  date: Date | null | undefined;
  comment?: string;
}

interface IAddDelay {
  open: boolean;
  handleClose: () => void;
}

const style = {
  position: 'absolute' as const,
  width: 600,
  maxHeight: 800,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
};

export const AddDelay: FC<IAddDelay> = ({open, handleClose}) => {
  const {control, reset, trigger, watch, handleSubmit} = useForm<IAddDelayForm>({
    defaultValues: {
      date: null,
    },
  });
  const {files, fileNames, setFiles} = useFileUpload();
  const {files: files2, fileNames: fileNames2, setFiles: setFiles2} = useFileUpload();
  const {id} = useParams();
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState<any>();
  const triggerQuery = useSetRecoilState(triggerReasonDelays);
  const {bus} = useContext(EventBusContext);

  const inputRef = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFiles(e);
  };

  const onChange2 = (e: ChangeEvent<HTMLInputElement>) => {
    setFiles2(e);
  };

  const onSubmit: SubmitHandler<IAddDelayForm> = async (data) => {
    setLoading(true);
    try {
      await ServiceRequestService.setReasonDelay(Number(id), {
        photos: {docFile: files[0], docFile2: files2[0]},
        comment: data.comment ? data.comment.trim() : '',
        date: data.date?.toISOString(),
      });

      setLoading(false);
      triggerQuery((v) => v + 1);
      reset();
      handleClose();
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const watchDate = watch('date');

  console.log(watchDate, watchDate && new Date(watchDate).toString());

  return (
    <Modal open={open}>
      <Box sx={style}>
        <Toolbar
          sx={{
            pl: {sm: 2},
            pr: {xs: 1, sm: 1},
            bgcolor: colors['bg--primary'],
          }}
        >
          <Typography variant="h6" component="div">
            Добавить недопуск
          </Typography>
        </Toolbar>
        <Stack sx={{p: '16px'}} direction="column" spacing={5}>
          <Stack direction="column" spacing={2}>
            <Controller
              control={control}
              name="date"
              render={({field: {value, onChange}}) => (
                <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Дата недопуска"
                    format={'dd.MM.yyyy HH:mm'}
                    value={value}
                    onError={(reason) => setDateError(reason)}
                    minutesStep={1}
                    disableFuture
                    onChange={onChange}
                  />
                </LocalizationProvider>
              )}
            />
            <Typography variant="body2" sx={{opacity: 0.8}}>
              Уведомление*
            </Typography>
            <Stack sx={{justifyContent: 'space-between'}} direction="row" spacing={2}>
              <Stack direction="row" alignItems={'center'} spacing={1}>
                {fileNames[0] && (
                  <>
                    <DocIconSwitcher docName={fileNames[0]} />
                    <Box sx={{maxWidth: '385px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                      <Tooltip placement="bottom-start" title={fileNames[0]}>
                        <Typography noWrap textOverflow="ellipsis">
                          {fileNames[0]}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </>
                )}
              </Stack>
              <Button
                onClick={() => inputRef?.current?.click()}
                sx={{
                  height: '40px',
                  fontSize: '0.87rem',
                  letterSpacing: '0',
                  backgroundColor: green[500],
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: green[600],
                  },
                }}
                variant="contained"
              >
                {!fileNames.length ? 'Выбрать фото' : 'Выбрать другое'}
              </Button>
            </Stack>
            <Typography variant="body2" sx={{opacity: 0.8}}>
              Акт недопуска
            </Typography>
            <Stack sx={{justifyContent: 'space-between'}} direction="row" spacing={2}>
              <Stack direction="row" alignItems={'center'} spacing={1}>
                {fileNames2[0] && (
                  <>
                    <DocIconSwitcher docName={fileNames2[0]} />
                    <Box sx={{maxWidth: '385px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                      <Tooltip placement="bottom-start" title={fileNames2[0]}>
                        <Typography noWrap textOverflow="ellipsis">
                          {fileNames2[0]}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </>
                )}
              </Stack>
              <Button
                onClick={() => inputRef2?.current?.click()}
                sx={{
                  height: '40px',
                  fontSize: '0.87rem',
                  letterSpacing: '0',
                  backgroundColor: green[500],
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: green[600],
                  },
                }}
                variant="contained"
              >
                {!fileNames2.length ? 'Выбрать фото' : 'Выбрать другое'}
              </Button>
            </Stack>
          </Stack>
          <ControlledInput
            control={control}
            name="comment"
            rules={{
              maxLength: {
                value: 400,
                message: 'Максимум 400 символов',
              },
            }}
            multiline
            rows={4}
            label="Комментарий"
            size="small"
            additionalFunc={() => {
              trigger('comment');
            }}
          />
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Button onClick={handleClose} color="error" variant="outlined">
              Отмена
            </Button>
            <LoadingButton
              disabled={!files[0] || !(!!watchDate && new Date(watchDate).toString() !== 'Invalid Date')}
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              loading={loading}
            >
              Добавить
            </LoadingButton>
          </Stack>
          <input
            type="file"
            ref={inputRef}
            accept=".jpg,.jpeg,.png,.webp"
            style={{display: 'none'}}
            onChange={onChange}
          />
          <input
            type="file"
            ref={inputRef2}
            accept=".jpg,.jpeg,.png,.webp"
            style={{display: 'none'}}
            onChange={onChange2}
          />
        </Stack>
      </Box>
    </Modal>
  );
};
